import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  useAttachPlayToGameplan,
  useCoachApprove,
  useCoachProposals,
  useCoachReject,
  useCoachRun,
  useGameplan,
  usePatchGameplan,
  type CoachEvent,
} from "../../hooks/useGameplans";
import { usePlaysList } from "../../hooks/usePlay";
import { useTemplates, type TemplateMeta } from "../../hooks/useTemplates";
import { SituationStageBar } from "../../components/SituationStageBar";
import { CampaignContextFeed } from "../../components/CampaignContextFeed";
import { GameplanMembersPanel } from "../../components/GameplanMembersPanel";
import { usePersona } from "../../hooks/usePersona";

function GamePlanDetailFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" style={{ color: "var(--danger)" }}>
      <p>Something went wrong loading this GamePlan.</p>
      <p style={{ fontSize: 12, color: "var(--text-2)" }}>{error.message}</p>
      <button type="button" className="btn" onClick={resetErrorBoundary}>
        Reload
      </button>
    </div>
  );
}

function GamePlanDetailInner() {
  const { workspaceId = "", campaignId = "" } = useParams<{
    workspaceId: string;
    campaignId: string;
  }>();
  const navigate = useNavigate();
  const persona = usePersona(workspaceId);
  const canEdit = persona === "owner" || persona === "cxo";
  const canAppendContext = canEdit || persona === "operator";
  const q = useGameplan(workspaceId, campaignId);
  const patch = usePatchGameplan(workspaceId, campaignId);
  const [attachOpen, setAttachOpen] = useState(false);

  if (q.isLoading) {
    return (
      <div className="main-inner" style={{ color: "var(--text-3)" }}>
        Loading GamePlan…
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <div className="main-inner" style={{ color: "var(--danger)" }}>
        Failed to load GamePlan.
      </div>
    );
  }
  const { campaign, plays } = q.data;

  const playsByParent = new Map<string | null, typeof plays>();
  for (const p of plays) {
    const key = p.spawnedByStepId ?? null;
    if (!playsByParent.has(key)) playsByParent.set(key, []);
    playsByParent.get(key)!.push(p);
  }
  const rootPlays = playsByParent.get(null) ?? [];

  return (
    <div className="main-inner" data-testid="gameplan-detail">
      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          className="btn"
          onClick={() => navigate(`/workspaces/${workspaceId}/gameplans`)}
          style={{ fontSize: 11, padding: "3px 8px" }}
        >
          ← All GamePlans
        </button>
      </div>
      <header style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 10,
            color: "var(--text-3)",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          GamePlan · {campaign.type.replace(/_/g, " ")}
        </div>
        <h1 style={{ margin: 0 }}>{campaign.title}</h1>
        {campaign.description ? (
          <p
            style={{
              marginTop: 6,
              fontSize: 13,
              color: "var(--text-2)",
              maxWidth: 800,
            }}
          >
            {campaign.description}
          </p>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <span
            data-testid="gameplan-status"
            style={{
              fontSize: 11,
              padding: "3px 10px",
              borderRadius: 4,
              background: "var(--bg-soft)",
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {campaign.status}
          </span>
          {canEdit && campaign.status !== "closed" ? (
            <>
              <select
                value={campaign.severity ?? ""}
                onChange={(e) =>
                  patch.mutate({
                    severity:
                      (e.target.value as
                        | ""
                        | "low"
                        | "medium"
                        | "high"
                        | "critical") || null,
                  })
                }
                data-testid="gameplan-severity-select"
                style={{ fontSize: 11, padding: "3px 6px" }}
              >
                <option value="">(no severity)</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
              <button
                type="button"
                className="btn"
                style={{ fontSize: 11, padding: "3px 8px" }}
                onClick={() => {
                  if (campaign.status === "resolving") {
                    patch.mutate({ status: "closed" });
                  } else {
                    patch.mutate({ status: "resolving" });
                  }
                }}
                data-testid="gameplan-status-action"
              >
                {campaign.status === "resolving" ? "Close" : "Mark resolving"}
              </button>
            </>
          ) : null}
        </div>
      </header>

      <SituationStageBar
        stages={campaign.situationStages}
        currentIndex={campaign.currentStageIndex}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,2fr) minmax(280px,1fr)",
          gap: 20,
          marginTop: 20,
        }}
      >
        <div data-testid="gameplan-play-tree">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "var(--text-3)",
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Plays in this GamePlan
            </div>
            {canEdit && campaign.status !== "closed" ? (
              <button
                type="button"
                className="btn"
                style={{ fontSize: 11, padding: "4px 8px" }}
                onClick={() => setAttachOpen(true)}
                data-testid="gameplan-attach-play"
              >
                + Attach play
              </button>
            ) : null}
          </div>
          {plays.length === 0 ? (
            <div
              style={{
                border: "1px dashed var(--border)",
                borderRadius: 8,
                padding: 24,
                textAlign: "center",
                color: "var(--text-3)",
                fontSize: 13,
              }}
            >
              No plays yet. Plays created from this GamePlan or attached to it
              will appear here.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {rootPlays.map((p) => (
                <PlayNode
                  key={p.id}
                  play={p}
                  workspaceId={workspaceId}
                  playsByParent={playsByParent}
                  depth={0}
                />
              ))}
              {/* If some plays have spawnedByStepId pointing to steps in
                  parents not in this campaign, still render them as roots
                  so they don't disappear. */}
              {plays
                .filter((p) => p.spawnedByStepId && !plays.some((parent) => parent.id === p.spawnedByStepId))
                .map((p) => (
                  <PlayNode
                    key={p.id}
                    play={p}
                    workspaceId={workspaceId}
                    playsByParent={playsByParent}
                    depth={0}
                  />
                ))}
            </ul>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <CampaignContextFeed
            workspaceId={workspaceId}
            campaignId={campaignId}
            canAppend={canAppendContext}
          />
          <CoachPanel
            workspaceId={workspaceId}
            campaignId={campaignId}
            canDecide={canEdit}
          />
          <section
            style={{
              border: "1px solid var(--border-2)",
              borderRadius: 8,
              padding: 14,
            }}
          >
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: 13,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: "var(--text-3)",
              }}
            >
              Members
            </h3>
            <GameplanMembersPanel
              workspaceId={workspaceId}
              campaignId={campaignId}
              canManage={canEdit}
            />
          </section>
        </div>
      </div>

      {attachOpen ? (
        <AttachPlayModal
          workspaceId={workspaceId}
          campaignId={campaignId}
          onClose={() => setAttachOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default function GamePlanDetail() {
  return (
    <ErrorBoundary FallbackComponent={GamePlanDetailFallback}>
      <GamePlanDetailInner />
    </ErrorBoundary>
  );
}

function CoachPanel({
  workspaceId,
  campaignId,
  canDecide,
}: {
  workspaceId: string;
  campaignId: string;
  canDecide: boolean;
}) {
  const proposals = useCoachProposals(workspaceId, campaignId, "proposed");
  const history = useCoachProposals(workspaceId, campaignId, "all");
  const approve = useCoachApprove(workspaceId, campaignId);
  const reject = useCoachReject(workspaceId, campaignId);
  const run = useCoachRun(workspaceId, campaignId);
  const [showHistory, setShowHistory] = useState(false);

  const proposed = proposals.data?.items ?? [];
  const all = history.data?.items ?? [];
  const past = all.filter((e) => e.status !== "proposed");

  if (proposals.isError) {
    return (
      <div data-testid="coach-panel">
        <div
          style={{
            fontSize: 12,
            color: "var(--danger)",
            padding: 12,
            border: "1px solid var(--danger)",
            borderRadius: 8,
          }}
        >
          Failed to load Coach proposals. Please refresh.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="coach-panel">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "var(--text-3)",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          The Coach
        </div>
        {canDecide ? (
          <button
            type="button"
            className="btn"
            style={{ fontSize: 11, padding: "4px 8px" }}
            onClick={() => run.mutate()}
            disabled={run.isPending}
            data-testid="coach-run"
          >
            {run.isPending ? "Thinking…" : "Ask the Coach"}
          </button>
        ) : null}
      </div>

      {proposed.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-3)",
            padding: 16,
            textAlign: "center",
            border: "1px dashed var(--border)",
            borderRadius: 8,
          }}
        >
          No pending proposals. The Coach watches this GamePlan and surfaces
          actions when state changes.
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {proposed.map((ev) => (
            <CoachCard
              key={ev.id}
              ev={ev}
              canDecide={canDecide}
              onApprove={() => approve.mutate({ eventId: ev.id })}
              onReject={() => reject.mutate({ eventId: ev.id })}
              pending={approve.isPending || reject.isPending}
            />
          ))}
        </ul>
      )}

      {past.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              fontSize: 11,
              color: "var(--text-3)",
              cursor: "pointer",
              fontWeight: 600,
            }}
            data-testid="coach-history-toggle"
          >
            {showHistory ? "▾" : "▸"} History ({past.length})
          </button>
          {showHistory ? (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "8px 0 0",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {past.slice(0, 20).map((ev) => (
                <li
                  key={ev.id}
                  style={{
                    fontSize: 11,
                    color: "var(--text-3)",
                    padding: 6,
                    borderRadius: 4,
                    background: "var(--bg-soft)",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: statusColor(ev.status),
                      marginRight: 6,
                    }}
                  >
                    {ev.status}
                  </span>
                  {ev.actionType} · {ev.reasoning.slice(0, 80)}
                  {ev.executionError ? (
                    <div style={{ color: "var(--danger)", marginTop: 2 }}>
                      {ev.executionError}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CoachCard({
  ev,
  canDecide,
  onApprove,
  onReject,
  pending,
}: {
  ev: CoachEvent;
  canDecide: boolean;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  return (
    <li
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 10,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
      data-testid={`coach-proposal-${ev.id}`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 3,
            background: "var(--accent-soft, #eef2ff)",
            color: "var(--accent, #4338ca)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {ev.actionType.replace(/_/g, " ")}
        </span>
        <span style={{ fontSize: 10, color: "var(--text-3)" }}>
          {new Date(ev.createdAt).toLocaleTimeString()}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "var(--text)" }}>{ev.reasoning}</div>
      <CoachPayloadSummary ev={ev} />
      {canDecide ? (
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <button
            type="button"
            className="btn primary"
            style={{ fontSize: 11, padding: "4px 10px" }}
            disabled={pending}
            onClick={onApprove}
            data-testid={`coach-approve-${ev.id}`}
          >
            Approve
          </button>
          <button
            type="button"
            className="btn"
            style={{ fontSize: 11, padding: "4px 10px" }}
            disabled={pending}
            onClick={onReject}
            data-testid={`coach-reject-${ev.id}`}
          >
            Reject
          </button>
        </div>
      ) : null}
    </li>
  );
}

function CoachPayloadSummary({ ev }: { ev: CoachEvent }) {
  const p = ev.actionPayload;
  if (ev.actionType === "spawn_play") {
    const tk = (p["template_key"] ?? {}) as Record<string, unknown>;
    return (
      <div style={{ fontSize: 11, color: "var(--text-2)" }}>
        <strong>spawn:</strong> {String(tk["industry"])}/{String(tk["trade"])}/
        {String(tk["archetype"])} · role={String(p["role"] ?? "support")}
      </div>
    );
  }
  if (ev.actionType === "escalate_severity") {
    return (
      <div style={{ fontSize: 11, color: "var(--text-2)" }}>
        <strong>severity:</strong> {String(p["from"] ?? "—")} → {String(p["to"])}
      </div>
    );
  }
  if (ev.actionType === "observe") {
    const v =
      typeof p["value"] === "string"
        ? p["value"]
        : JSON.stringify(p["value"]);
    return (
      <div style={{ fontSize: 11, color: "var(--text-2)" }}>
        <strong>observe:</strong> {String(p["key"])} = {v} [
        {String(p["confidence"] ?? "suspected")}]
      </div>
    );
  }
  if (ev.actionType === "flag_human") {
    return (
      <div
        style={{
          fontSize: 12,
          color: "var(--text)",
          background: "var(--bg-soft)",
          padding: 6,
          borderRadius: 4,
        }}
      >
        {String(p["message"] ?? "")}
      </div>
    );
  }
  return null;
}

function statusColor(s: CoachEvent["status"]): string {
  if (s === "executed" || s === "auto_approved") return "#16a34a";
  if (s === "rejected") return "#9a3412";
  if (s === "failed") return "#dc2626";
  return "var(--text-3)";
}

function AttachPlayModal({
  workspaceId,
  campaignId,
  onClose,
}: {
  workspaceId: string;
  campaignId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const list = usePlaysList(workspaceId);
  const templates = useTemplates();
  const attach = useAttachPlayToGameplan(workspaceId, campaignId);
  const [mode, setMode] = useState<"existing" | "template">("existing");
  const [role, setRole] = useState<"primary" | "secondary" | "support">(
    "support",
  );
  const [picked, setPicked] = useState<string | null>(null);
  const [pickedTemplate, setPickedTemplate] = useState<TemplateMeta | null>(
    null,
  );
  const candidates = (list.data?.items ?? []).filter((p) => {
    if (p.state === "completed" || p.state === "abandoned") return false;
    const cId = (p as { campaignId?: string | null }).campaignId ?? null;
    if (cId && cId !== campaignId) return false;
    return true;
  });
  const canSubmit =
    mode === "existing" ? !!picked : !!pickedTemplate;
  return (
    <div
      role="dialog"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          borderRadius: 10,
          width: 520,
          maxWidth: "92vw",
          maxHeight: "80vh",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
        data-testid="gameplan-attach-modal"
      >
        <h2 style={{ margin: 0 }}>Add a play</h2>
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          Either attach an existing play, or pick a template to decompose a
          new play directly into this GamePlan. BAU (recurring) plays can&apos;t
          be members.
        </div>
        <div
          role="tablist"
          style={{
            display: "flex",
            gap: 4,
            borderBottom: "1px solid var(--border)",
            marginTop: 4,
          }}
        >
          {(["existing", "template"] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              data-testid={`gameplan-attach-tab-${m}`}
              style={{
                padding: "6px 12px",
                background: "transparent",
                border: 0,
                borderBottom:
                  mode === m
                    ? "2px solid var(--accent)"
                    : "2px solid transparent",
                color: mode === m ? "var(--accent)" : "var(--text-2)",
                fontWeight: mode === m ? 600 : 500,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              {m === "existing" ? "Attach existing" : "From template"}
            </button>
          ))}
        </div>
        <label style={{ fontSize: 12, color: "var(--text-2)" }}>
          Role
          <select
            value={role}
            onChange={(e) =>
              setRole(e.target.value as "primary" | "secondary" | "support")
            }
            style={{
              display: "block",
              width: "100%",
              padding: 6,
              marginTop: 4,
            }}
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="support">Support</option>
          </select>
        </label>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 6,
            maxHeight: 280,
            overflowY: "auto",
            background: "var(--bg-soft)",
          }}
        >
          {mode === "existing" ? (
            list.isLoading ? (
              <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)" }}>
                Loading plays…
              </div>
            ) : candidates.length === 0 ? (
              <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)" }}>
                No eligible plays in this workspace. Switch to ‘From template’
                to spawn a new one.
              </div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {candidates.map((p) => (
                  <li
                    key={p.id}
                    onClick={() => setPicked(p.id)}
                    data-testid={`attach-candidate-${p.id}`}
                    style={{
                      padding: 10,
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      background:
                        picked === p.id
                          ? "var(--accent-soft)"
                          : "transparent",
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {p.archetype}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                        {p.id} · {new Date(p.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        padding: "2px 6px",
                        borderRadius: 3,
                        background: "var(--bg)",
                        color: "var(--text-2)",
                        textTransform: "capitalize",
                        fontWeight: 600,
                      }}
                    >
                      {p.state}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : templates.isLoading ? (
            <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)" }}>
              Loading templates…
            </div>
          ) : (templates.data?.templates.length ?? 0) === 0 ? (
            <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)" }}>
              No templates available.
            </div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {templates.data!.templates.map((t) => {
                const key = `${t.industry}/${t.trade}/${t.archetype}`;
                const isPicked =
                  pickedTemplate &&
                  `${pickedTemplate.industry}/${pickedTemplate.trade}/${pickedTemplate.archetype}` ===
                    key;
                return (
                  <li
                    key={key}
                    onClick={() => setPickedTemplate(t)}
                    data-testid={`attach-template-${key}`}
                    style={{
                      padding: 10,
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      background: isPicked
                        ? "var(--accent-soft)"
                        : "transparent",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {t.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-3)",
                        marginTop: 2,
                      }}
                    >
                      {t.industry} · {t.trade} · {t.archetype}
                    </div>
                    {t.description ? (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--text-2)",
                          marginTop: 3,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {t.description}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {attach.error ? (
          <div style={{ fontSize: 12, color: "var(--danger)" }}>
            {(attach.error as Error).message}
          </div>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 4,
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={attach.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!canSubmit || attach.isPending}
            data-testid="gameplan-attach-submit"
            onClick={() => {
              if (mode === "existing") {
                if (!picked) return;
                attach.mutate(
                  { playId: picked, campaignRole: role },
                  { onSuccess: () => onClose() },
                );
              } else {
                if (!pickedTemplate) return;
                attach.mutate(
                  {
                    templateKey: {
                      industry: pickedTemplate.industry,
                      trade: pickedTemplate.trade,
                      archetype: pickedTemplate.archetype,
                    },
                    campaignRole: role,
                  },
                  {
                    onSuccess: (r) => {
                      onClose();
                      // Land the user on the new play so they can review it.
                      if (r?.playId) {
                        navigate(`/workspaces/${workspaceId}/plays/${r.playId}`);
                      }
                    },
                  },
                );
              }
            }}
          >
            {attach.isPending
              ? mode === "existing"
                ? "Attaching…"
                : "Creating play…"
              : mode === "existing"
                ? "Attach"
                : "Create play"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface PlayRow {
  id: string;
  archetype: string;
  state: string;
  campaignRole: "primary" | "secondary" | "support" | null;
  spawnedByStepId: string | null;
  createdAt: string;
}

function PlayNode({
  play,
  workspaceId,
  playsByParent,
  depth,
}: {
  play: PlayRow;
  workspaceId: string;
  playsByParent: Map<string | null, PlayRow[]>;
  depth: number;
}) {
  const navigate = useNavigate();
  // No deep nesting since v1 doesn't track step→play causal pointers
  // in the playsByParent map (it's keyed on spawned_by_step_id which is
  // a step id; the map key is null for roots — anything indented relies
  // on future Phase data). For now this renders flat with depth=0.
  const children: PlayRow[] = [];
  return (
    <>
      <li
        style={{
          marginLeft: depth * 16,
          marginBottom: 6,
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: 10,
          background: "var(--bg)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
        onClick={() => navigate(`/workspaces/${workspaceId}/plays/${play.id}`)}
        data-testid={`gameplan-play-node-${play.id}`}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{play.archetype}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>
            {new Date(play.createdAt).toLocaleString()}
          </div>
        </div>
        {play.campaignRole ? (
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 3,
              background: "var(--bg-soft)",
              color: "var(--text-2)",
              textTransform: "capitalize",
              letterSpacing: 0.3,
            }}
          >
            {play.campaignRole}
          </span>
        ) : null}
        <span
          style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 3,
            background:
              play.state === "running"
                ? "#dcfce7"
                : play.state === "completed"
                  ? "#dbeafe"
                  : play.state === "abandoned"
                    ? "#fee2e2"
                    : "#f1f5f9",
            color: "var(--text-2)",
            textTransform: "capitalize",
            fontWeight: 600,
          }}
        >
          {play.state}
        </span>
      </li>
      {children.map((c) => (
        <PlayNode
          key={c.id}
          play={c}
          workspaceId={workspaceId}
          playsByParent={playsByParent}
          depth={depth + 1}
        />
      ))}
    </>
  );
}
